import config from '../config';

const RECONNECT_BASE_MS = 1000;
const MAX_RECONNECT_MS = 30000;
const IDLE_TIMEOUT_MS = 90000;

function toWsUrl(httpUrl: string): string {
  return `${httpUrl.replace(/^http/, 'ws')  }/ws`;
}

type GetToken = () => Promise<string | null>;

export type OrdrWsConnectionState = 'disconnected' | 'connecting' | 'connected';

export type WebSocketMessage = {
  direction: 'in' | 'out';
  timestamp: Date;
  data: string;
};

type InboundEnvelope = {
  type: string;
  data?: unknown;
};

export type OrdrWebSocketClientOptions = {
  getToken: GetToken;
  deviceId: string;
  onStateChange?: (state: OrdrWsConnectionState) => void;
  onMessageLog?: (msg: WebSocketMessage) => void;
  onMessage?: (type: string, data: unknown) => void;
};

/**
 * Plain transport for the ordr-service KDS websocket: connect with backoff,
 * idle detection and station (re)subscription. It has no knowledge of what
 * the messages mean — everything inbound is forwarded through the option
 * callbacks, and the interpretation lives with the caller.
 * A client instance is single-use: once close() is called it stays closed.
 */
export class OrdrWebSocketClient {
  private ws: WebSocket | null = null;
  private closed = false;
  private connecting = false;
  private state: OrdrWsConnectionState = 'disconnected';
  private reconnectAttempt = 0;
  private reconnectTimer: number | undefined;
  private idleTimer: number | undefined;
  private stations = new Set<number>();
  private lastToken: string | null = null;

  constructor(private readonly options: OrdrWebSocketClientOptions) {}

  setStations(stationIds: number[]): void {
    const next = new Set(stationIds);
    const unchanged = next.size === this.stations.size && [...next].every((id) => this.stations.has(id));
    this.stations = next;
    if (!unchanged) this.resubscribe();
  }

  connect(): void {
    if (this.closed) return;
    this.setState('connecting');
    void this.doConnect();
  }

  close(): void {
    this.closed = true;
    this.clearTimers();
    this.ws?.close();
    this.ws = null;
    this.setState('disconnected');
  }

  private resubscribe(): void {
    if (!this.lastToken || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.send({
      type: 'subscribe.v1.kds',
      data: {
        jwt: this.lastToken,
        stations: [...this.stations].map(String),
        deviceId: this.options.deviceId,
      },
    });
  }

  private hasLiveSocket(): boolean {
    return this.ws !== null && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN);
  }

  private async doConnect(): Promise<void> {
    // A connect can be requested from several places (initial connect, backoff
    // timer, idle restart); make duplicates harmless.
    if (this.closed || this.connecting || this.hasLiveSocket()) return;
    this.connecting = true;

    try {
      const token = await this.options.getToken();
      if (this.closed) return;
      if (!token) {
        // Not authenticated (yet) — retry with backoff instead of going dead.
        this.setState('disconnected');
        this.scheduleReconnect();
        return;
      }

      // TODO: restore dynamic URL before merging
      const url = toWsUrl(config.ordrServiceUrl);
      // const url = 'ws://localhost:8080/ws';
      const ws = new WebSocket(url);

      this.ws = ws;

      ws.onopen = () => {
        if (this.ws !== ws) return;
        this.reconnectAttempt = 0;
        this.lastToken = token;
        // Stay in 'connecting' until subscribe.v1.kds.ok is received

        if (this.stations.size > 0) {
          this.resubscribe();
        }

        this.resetIdleTimer();
      };

      ws.onmessage = (ev) => {
        if (this.ws !== ws) return;
        this.handleMessage(ev);
      };

      ws.onclose = () => {
        if (this.ws !== ws) return;
        this.stopIdleTimer();
        this.setState('disconnected');
        if (!this.closed) this.scheduleReconnect();
      };

      ws.onerror = () => {
        // onclose fires after this
      };
    } catch {
      // Token fetch or socket construction failed — treat like a dropped connection.
      if (!this.closed) {
        this.setState('disconnected');
        this.scheduleReconnect();
      }
    } finally {
      this.connecting = false;
    }
  }

  private handleMessage(ev: MessageEvent): void {
    this.resetIdleTimer();
    const raw = typeof ev.data === 'string' ? ev.data : '[binary]';

    let envelope: InboundEnvelope;
    try {
      envelope = JSON.parse(raw);
    } catch {
      console.warn('[orders-ws] received non-JSON message, ignoring', raw.slice(0, 200));
      return;
    }

    if (!envelope.type) {
      console.warn('[orders-ws] message missing "type" field, ignoring', envelope);
      return;
    }

    this.options.onMessageLog?.({
      direction: 'in',
      timestamp: new Date(),
      data: raw,
    });

    // Subscription confirmed — now we're fully connected
    if (envelope.type === 'subscribe.v1.kds.ok') {
      this.setState('connected');
      // Fall through to dispatch to registered handlers
    }

    // Respond to server pings
    if (envelope.type === 'ping.v1') {
      this.send({ type: 'pong.v1', data: envelope.data });
      return;
    }

    this.options.onMessage?.(envelope.type, envelope.data);
  }

  private send(payload: unknown): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const json = JSON.stringify(payload);
    this.ws.send(json);
    this.options.onMessageLog?.({
      direction: 'out',
      timestamp: new Date(),
      data: json,
    });
  }

  private resetIdleTimer(): void {
    clearTimeout(this.idleTimer);
    this.idleTimer = window.setTimeout(() => {
      // No message received for 90s — connection is dead, force reconnect
      this.ws?.close();
    }, IDLE_TIMEOUT_MS);
  }

  private stopIdleTimer(): void {
    clearTimeout(this.idleTimer);
    this.idleTimer = undefined;
  }

  private scheduleReconnect(): void {
    if (this.closed) return;
    this.clearTimers();
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** this.reconnectAttempt, MAX_RECONNECT_MS);
    this.reconnectAttempt++;
    this.reconnectTimer = window.setTimeout(() => this.connect(), delay);
  }

  private clearTimers(): void {
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    this.stopIdleTimer();
  }

  private setState(state: OrdrWsConnectionState): void {
    if (this.state === state) return;
    this.state = state;
    this.options.onStateChange?.(state);
  }
}
